import styled from "@emotion/styled";
import { motion } from "framer-motion";
import { type FC, type ReactNode, useRef } from "react";
import { FocusScope, useDialog } from "react-aria";
import { Button } from "react-aria-components";

export interface FloatingInfoCardProps {
  children: ReactNode;
  onPress: () => void;
  onClose: () => void;
  ariaLabel?: string;
}

const CardRoot = styled(motion.div)`
  position: relative;
  border: 1px solid var(--info-card-border-color, rgba(95, 31, 143, 0.24));
  background: var(--info-card-bg);
  backdrop-filter: blur(24px);
  border-radius: 20px;
  width: 100%;
  overflow: hidden;
  transition: border-color 0.15s ease;

  @supports (corner-shape: squircle) {
    corner-shape: squircle;
    border-radius: 45px;
  }
`;

const ActionArea = styled(Button)`
  display: block;
  width: 100%;
  padding: 0.7em 3.4em 0.7em 1em;
  border: none;
  background: transparent;
  cursor: pointer;
  font: inherit;
  font-size: var(--font-size, 1rem);
  line-height: 1.4;
  letter-spacing: -0.02em;
  text-align: left;
  color: inherit;
`;

const CloseButtonStyled = styled(Button)`
  position: absolute;
  top: 0.75em;
  right: 0.75em;
  width: 2.1em;
  height: 2.1em;
  border-radius: 50%;
  border: none;
  background: rgba(95, 31, 143, 0.12);
  color: rgba(95, 31, 143, 0.6);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;

  &::before {
    content: "";
    position: absolute;
    inset: 50%;
    width: 44px;
    height: 44px;
    translate: -50% -50%;
  }

  &[data-hovered] {
    background: rgba(95, 31, 143, 0.2);
  }

  svg {
    width: 0.85em;
    height: 0.85em;
  }
`;

const FloatingInfoCard: FC<FloatingInfoCardProps> = ({
  children,
  onPress,
  onClose,
  ariaLabel
}) => {
  const dialogRef = useRef<HTMLDivElement>(null);
  const { dialogProps } = useDialog(
    { role: "alertdialog", "aria-label": ariaLabel },
    dialogRef
  );
  return (
    <FocusScope autoFocus restoreFocus>
      <CardRoot
        {...(dialogProps as Record<string, unknown>)}
        ref={dialogRef}
        initial={{ opacity: 0, y: -16 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -16 }}
        transition={{ duration: 0.3, ease: [0.4, 0, 0.2, 1] }}
      >
        <ActionArea onPress={onPress}>{children}</ActionArea>
        <CloseButtonStyled aria-label="Close" onPress={onClose}>
          <svg viewBox="0 0 16 16" fill="none" aria-hidden="true">
            <path
              d="M4 4l8 8M12 4l-8 8"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
            />
          </svg>
        </CloseButtonStyled>
      </CardRoot>
    </FocusScope>
  );
};

export default FloatingInfoCard;
